import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjsUtils from '@attain-apps/rxjs-utils'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type * as me from '../../../me'
import type { Feature } from '../feature'
import * as gql from '../gql'
import type * as models from '../models'

export const onLoadEdgesRequest = store.effects.create(
  (feature: Feature, meFeature: me.Feature) =>
    feature.actions.loadEdges.request.observe().pipe(
      rxjsUtils.latestFrom(action => ({
        action: rxjs.of(action),
        context: meFeature.contexts.selectors.zoneContext.asObservable,
        nodes: feature.selectors.getNodes({ chartId: action.data.chartId })
          .asObservable,
      })),
      rxjs.switchMap(({ action, nodes, context }) => {
        // if we don't have context, we can't proceed.
        if (!context) {
          return rxjs.of(store.effects.SKIP)
        }

        const {
          data: { edgesFrom },
        } = action

        return _getQuery({ context, edgesFrom, nodes }).pipe(
          rxjs.map(edges =>
            feature.actions.loadEdges.success.from(action).create({
              request: action.data,
              response: { edges },
            }),
          ),
          rxjs.catchError((error: unknown) =>
            rxjs.of(
              feature.actions.loadEdges.error.from(action).create({
                error:
                  error instanceof Error ? error : new Error(String(error)),
                request: action.data,
              }),
            ),
          ),
        )
      }),
    ),
)

/** TODO:  */
function _getQuery({
  context,
  edgesFrom,
  nodes,
}: {
  readonly edgesFrom: ents.core.display.tree.Tree['edgesFrom']
  readonly nodes: readonly ents.core.display.tree.Node[]
  readonly context: ents.core.admin.Context.ForZone
}): rxjs.Observable<readonly models.Edge[]> {
  if (nodes.length === 0) {
    return rxjs.of([])
  }

  let query: ents.core.dms.data.query.Query<models.Edge>
  if (TypeChecker.isNullish(edgesFrom)) {
    const [node] = nodes

    query = {
      filter: {
        '!=': [
          {
            '@^': 'parent',
          },
          null,
        ],
      },
      selection: {
        '{}': {
          childId: { '@.': ents.core.dms.data.Record.Id.KEY },
          parentId: {
            '@^': 'parent',
          },
        },
      },
      target: ents.core.dms.models.Dimension.Id.from(node.modelId),
    }
  } else if (typeof edgesFrom === 'object') {
    query = {
      selection: {
        '{}': {
          childId: { '@>': [{ '@.': edgesFrom.child }, 'recordId'] },
          leaderType: { '@.': '!leader_type' },
          parentId: { '@>': [{ '@.': edgesFrom.parent }, 'recordId'] },
          recordId: { '@.': ents.core.dms.data.Record.Id.KEY },
        },
      },
      target: edgesFrom.association,
    }
  } else {
    // FIXME: give better error
    throw new Error('Not handled case')
  }

  return gql.queries.edges.execute({
    ...context,
    query: JSON.stringify(query),
  })
}
